import { useState, useRef, useEffect } from 'react';
import { X, Settings, Image as ImageIcon, UserPlus, Lock, Palette } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface SettingsModalProps {
  onClose: () => void;
  isDark: boolean;
}

interface CustomCategory {
  id: string;
  label: string;
  color: string;
}

export default function SettingsModal({ onClose, isDark }: SettingsModalProps) {
  const { profile, user } = useAuth();
  const [nameInput, setNameInput] = useState(profile?.name || '');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [message, setMessage] = useState({ text: '', type: '' });
  const [categories, setCategories] = useState<CustomCategory[]>([]);
  const [newCategory, setNewCategory] = useState({ label: '', color: '#3b82f6' });
  const avatarFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    const { data } = await supabase
      .from('custom_categories')
      .select('*')
      .eq('user_id', user?.id);

    if (data) {
      setCategories(data);
    }
  };

  const handleSaveName = async () => {
    if (!nameInput.trim()) {
      toast.error('Имя не может быть пустым.');
      return;
    }

    const { error } = await supabase
      .from('profiles')
      .update({ name: nameInput, updated_at: new Date().toISOString() })
      .eq('id', user?.id);

    if (error) {
      toast.error('Ошибка обновления имени.');
      console.error('Error updating name:', error);
    } else {
      toast.success('Имя успешно обновлено.');
      // Перезагружаем страницу, чтобы обновить профиль
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  };

  const handleSavePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Новый пароль должен быть не менее 6 символов.');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast.error('Новые пароли не совпадают.');
      return;
    }

    const { error } = await supabase.auth.updateUser({ password: newPassword });

    if (error) {
      toast.error('Ошибка обновления пароля.');
      console.error('Error updating password:', error);
    } else {
      setOldPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      toast.success('Пароль успешно обновлен.');
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      toast.error('Файл не выбран.');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Файл слишком большой (макс. 2МБ).');
      return;
    }

    toast.info('Загрузка фото...');

    const fileExt = file.name.split('.').pop();
    const fileName = `${user?.id}/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(fileName, file, { upsert: true });

    if (uploadError) {
      toast.error('Ошибка загрузки фото.');
      console.error('Upload error:', uploadError);
      return;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('avatars')
      .getPublicUrl(fileName);

    const { error: updateError } = await supabase
      .from('profiles')
      .update({ avatar: publicUrl, updated_at: new Date().toISOString() })
      .eq('id', user?.id);

    if (updateError) {
      toast.error('Ошибка обновления профиля.');
      console.error('Update error:', updateError);
    } else {
      toast.success('Фото успешно загружено.');
      // Перезагружаем страницу, чтобы обновить аватар
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
    e.target.value = '';
  };

  const addCategory = async () => {
    if (!newCategory.label) {
      toast.error('Введите название категории');
      return;
    }

    const { error } = await supabase.from('custom_categories').insert({
      user_id: user?.id,
      label: newCategory.label,
      color: newCategory.color,
    });

    if (error) {
      toast.error('Ошибка добавления категории');
      return;
    }

    toast.success('Категория добавлена');
    setNewCategory({ label: '', color: '#3b82f6' });
    loadCategories();
  };

  const deleteCategory = async (id: string) => {
    await supabase.from('custom_categories').delete().eq('id', id);
    toast.success('Категория удалена');
    loadCategories();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div
        className={`w-full max-w-2xl rounded-3xl p-8 shadow-2xl relative h-[90vh] flex flex-col ${
          isDark ? 'bg-slate-800 text-white border border-slate-700' : 'bg-white'
        }`}
      >
        <button onClick={onClose} className="absolute top-6 right-6 text-slate-400 hover:text-slate-600">
          <X size={24} />
        </button>
        <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <Settings size={24} className="text-blue-500" /> Настройки профиля
        </h2>


        <div className="flex-1 overflow-y-auto pr-3 space-y-8 custom-scroll">
          {/* Изменение фото */}
          <div
            className={`p-4 rounded-xl border ${
              isDark ? 'bg-slate-900/50 border-slate-700' : 'bg-slate-50 border-slate-200'
            }`}
          >
            <h3 className="font-bold mb-3 flex items-center gap-2">
              <ImageIcon size={18} className="text-purple-500" /> Фото профиля
            </h3>
            <div className="flex items-center gap-4">
              <img
                src={profile?.avatar || '/placeholder.svg?height=150&width=150'}
                className={`w-16 h-16 rounded-full object-cover border-2 ${
                  isDark ? 'border-slate-600' : 'border-slate-300'
                }`}
                alt="Аватар"
              />

              <input
                type="file"
                ref={avatarFileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
              />
              <button
                onClick={() => avatarFileInputRef.current?.click()}
                className="px-4 py-2 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors text-sm"
              >
                Загрузить / Сменить фото
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              *(Примечание: Максимальный размер файла 2МБ)
            </p>
          </div>

          {/* Изменение имени */}
          <div
            className={`p-4 rounded-xl border ${
              isDark ? 'bg-slate-900/50 border-slate-700' : 'bg-slate-50 border-slate-200'
            }`}
          >
            <h3 className="font-bold mb-3 flex items-center gap-2">
              <UserPlus size={18} className="text-blue-500" /> Общие данные
            </h3>
            <label className="text-xs text-slate-500 uppercase font-bold mb-1 block">Имя</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                className={`flex-1 p-2 rounded-lg border focus:outline-none ${
                  isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-300'
                }`}
              />
              <button
                onClick={handleSaveName}
                className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
              >
                Сохранить
              </button>
            </div>
          </div>

          {/* Изменение пароля */}
          <div
            className={`p-4 rounded-xl border ${
              isDark ? 'bg-slate-900/50 border-slate-700' : 'bg-slate-50 border-slate-200'
            }`}
          >
            <h3 className="font-bold mb-3 flex items-center gap-2">
              <Lock size={18} className="text-red-500" /> Смена пароля
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-500 uppercase font-bold mb-1 block">
                  Старый пароль
                </label>
                <input
                  type="password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  className={`w-full p-2 rounded-lg border focus:outline-none ${
                    isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-300'
                  }`}
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase font-bold mb-1 block">
                  Новый пароль
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={`w-full p-2 rounded-lg border focus:outline-none ${
                    isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-300'
                  }`}
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase font-bold mb-1 block">
                  Повторите новый пароль
                </label>
                <input
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className={`w-full p-2 rounded-lg border focus:outline-none ${
                    isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-300'
                  }`}
                />
              </div>
              <button
                onClick={handleSavePassword}
                className="w-full py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition-colors"
              >
                Сменить пароль
              </button>
            </div>
          </div>

          {/* Кастомные категории контактов */}
          <div
            className={`p-4 rounded-xl border ${
              isDark ? 'bg-slate-900/50 border-slate-700' : 'bg-slate-50 border-slate-200'
            }`}
          >
            <h3 className="font-bold mb-3 flex items-center gap-2">
              <Palette size={18} className="text-green-500" /> Кастомные категории контактов
            </h3>

            <div className="space-y-3 mb-4">
              {categories.map((cat) => (
                <div
                  key={cat.id}
                  className="flex items-center justify-between p-3 bg-white dark:bg-slate-700 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-lg"
                      style={{ backgroundColor: cat.color }}
                    />
                    <span className="font-medium">{cat.label}</span>
                  </div>
                  <button
                    onClick={() => deleteCategory(cat.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X size={18} />
                  </button>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Название категории"
                  value={newCategory.label}
                  onChange={(e) => setNewCategory({ ...newCategory, label: e.target.value })}
                  className={`flex-1 p-2 rounded-lg border focus:outline-none ${
                    isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-300'
                  }`}
                />
                <input
                  type="color"
                  value={newCategory.color}
                  onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                  className="w-16 h-10 rounded-lg cursor-pointer"
                />
              </div>
              <button
                onClick={addCategory}
                className="w-full py-2 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors"
              >
                Добавить категорию
              </button>
            </div>
          </div>
        </div>

        <div
          className={`flex justify-end pt-6 mt-4 border-t ${
            isDark ? 'border-slate-700' : 'border-slate-100'
          }`}
        >
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700"
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
}
